import React, { useState, lazy, Suspense, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Tabs,
  Tab,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
  Skeleton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Person as PersonIcon,
  AccountCircle as AccountIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  Palette as PaletteIcon,
  Notifications as NotificationsIcon,
  Message as MessageIcon,
  Storage as StorageIcon
} from '@mui/icons-material';

// Lazy load tab components
const ProfileTab = lazy(() => import('./tabs/ProfileTab'));
const AccountTab = lazy(() => import('./tabs/AccountTab'));
const PrivacyTab = lazy(() => import('./tabs/PrivacyTab'));
const SecurityTab = lazy(() => import('./tabs/SecurityTab'));
const DisplayTab = lazy(() => import('./tabs/DisplayTab'));
const NotificationsTab = lazy(() => import('./tabs/NotificationsTab'));
const MessagingTab = lazy(() => import('./tabs/MessagingTab'));
const DataTab = lazy(() => import('./tabs/DataTab'));

interface TabConfig {
  label: string;
  value: string;
  icon: React.ReactElement;
  component: React.LazyExoticComponent<React.FC>;
}

const tabs: TabConfig[] = [
  { label: 'Profile', value: 'profile', icon: <PersonIcon />, component: ProfileTab },
  { label: 'Account', value: 'account', icon: <AccountIcon />, component: AccountTab },
  { label: 'Privacy', value: 'privacy', icon: <VisibilityIcon />, component: PrivacyTab },
  { label: 'Security', value: 'security', icon: <LockIcon />, component: SecurityTab },
  { label: 'Display', value: 'display', icon: <PaletteIcon />, component: DisplayTab },
  { label: 'Notifications', value: 'notifications', icon: <NotificationsIcon />, component: NotificationsTab },
  { label: 'Messaging', value: 'messaging', icon: <MessageIcon />, component: MessagingTab },
  { label: 'Data', value: 'data', icon: <StorageIcon />, component: DataTab },
];

/**
 * Main User Settings Page
 *
 * Layout:
 * - Desktop: Sidebar navigation + content area
 * - Mobile: Top tabs or accordion
 *
 * Features:
 * - Lazy-loaded tab components
 * - URL parameter for active tab
 * - Responsive layout
 */
const UserSettingsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { tab } = useParams<{ tab?: string }>();
  const navigate = useNavigate();

  // Determine active tab from URL param, default to 'profile'
  const activeTab = tab || 'profile';

  // Redirect invalid tabs to profile
  useEffect(() => {
    const validTabs = tabs.map(t => t.value);
    if (tab && !validTabs.includes(tab)) {
      navigate('/settings/profile', { replace: true });
    } else if (!tab) {
      // If no tab specified, redirect to profile
      navigate('/settings/profile', { replace: true });
    }
  }, [tab, navigate]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    navigate(`/settings/${newValue}`);
  };

  // Get active component
  const ActiveComponent = tabs.find(t => t.value === activeTab)?.component || ProfileTab;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Page Header */}
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
        User Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Manage your account, preferences, and privacy settings
      </Typography>

      {/* Desktop Layout: Sidebar + Content */}
      {!isMobile ? (
        <Box sx={{ display: 'flex', gap: 3, mt: 3 }}>
          {/* Sidebar Navigation */}
          <Paper sx={{ width: 240, flexShrink: 0 }}>
            <List>
              {tabs.map(tab => (
                <ListItem
                  key={tab.value}
                  component="button"
                  onClick={() => navigate(`/settings/${tab.value}`)}
                  sx={{
                    borderLeft: 3,
                    borderColor: activeTab === tab.value ? 'primary.main' : 'transparent',
                    bgcolor: activeTab === tab.value ? 'action.selected' : 'transparent',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  <ListItemIcon sx={{ color: 'text.primary' }}>{tab.icon}</ListItemIcon>
                  <ListItemText
                    primary={tab.label}
                    primaryTypographyProps={{
                      color: 'text.primary'
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>

          {/* Content Area */}
          <Box sx={{ flex: 1 }}>
            <Suspense fallback={<TabSkeleton />}>
              <ActiveComponent />
            </Suspense>
          </Box>
        </Box>
      ) : (
        /* Mobile Layout: Top Tabs */
        <Box sx={{ mt: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            {tabs.map(tab => (
              <Tab
                key={tab.value}
                label={tab.label}
                value={tab.value}
                icon={tab.icon}
                iconPosition="start"
              />
            ))}
          </Tabs>

          <Box sx={{ mt: 3 }}>
            <Suspense fallback={<TabSkeleton />}>
              <ActiveComponent />
            </Suspense>
          </Box>
        </Box>
      )}
    </Container>
  );
};

/**
 * Loading skeleton for tab content
 */
const TabSkeleton: React.FC = () => (
  <Paper sx={{ p: 3 }}>
    <Skeleton variant="text" width="40%" height={40} />
    <Skeleton variant="rectangular" width="100%" height={200} sx={{ mt: 2 }} />
    <Skeleton variant="rectangular" width="100%" height={200} sx={{ mt: 2 }} />
  </Paper>
);

export default UserSettingsPage;
